/**
 * Review & Confirm Step (Step 4)
 *
 * Displays a read-only summary of all wizard selections before
 * the tenant is created. Shows name, slug, description, selected
 * templates, quotas, environments, and features.
 */

import { CheckCircle, XCircle } from "lucide-react";
import type { ReactElement } from "react";

import type { TenantFeatures } from "../../pages/admin/tenants/types";
import { FEATURE_LABELS } from "../../pages/admin/tenants/types";

import type { PlanQuotasData } from "./PlanQuotasStep";
import type { TenantDetailsData } from "./TenantDetailsStep";
import { VERTICAL_TEMPLATES } from "./VerticalTemplateStep";

interface ReviewConfirmStepProps {
  readonly tenantDetails: TenantDetailsData;
  readonly selectedTemplates: readonly string[];
  readonly planQuotas: PlanQuotasData;
}

export function ReviewConfirmStep({
  tenantDetails,
  selectedTemplates,
  planQuotas,
}: ReviewConfirmStepProps): ReactElement {
  const selectedTemplateNames = VERTICAL_TEMPLATES.filter((t) =>
    selectedTemplates.includes(t.id)
  ).map((t) => t.name);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Review & Confirm</h2>
        <p className="mt-1 text-sm text-foreground-muted">
          Review the configuration below before creating the tenant.
        </p>
      </div>

      {/* Tenant Details */}
      <div className="card">
        <h3 className="text-base font-semibold mb-3">Tenant Details</h3>
        <dl className="space-y-2">
          <div className="flex justify-between">
            <dt className="text-sm text-foreground-muted">Name</dt>
            <dd className="text-sm font-medium">{tenantDetails.name}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-sm text-foreground-muted">Slug</dt>
            <dd className="text-sm font-mono">{tenantDetails.slug}</dd>
          </div>
          {tenantDetails.description && (
            <div className="flex justify-between">
              <dt className="text-sm text-foreground-muted">Description</dt>
              <dd className="text-sm max-w-xs text-right">{tenantDetails.description}</dd>
            </div>
          )}
        </dl>
      </div>

      {/* Templates */}
      <div className="card">
        <h3 className="text-base font-semibold mb-3">Vertical Templates</h3>
        {selectedTemplateNames.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {selectedTemplateNames.map((name) => (
              <span
                key={name}
                className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary"
              >
                {name}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-foreground-muted">No templates selected</p>
        )}
      </div>

      {/* Quotas */}
      <div className="card">
        <h3 className="text-base font-semibold mb-3">Resource Limits</h3>
        <dl className="grid gap-2 sm:grid-cols-2">
          <QuotaItem label="Max Verticals" value={String(planQuotas.quotas.maxVerticals)} />
          <QuotaItem
            label="Max Products/Vertical"
            value={String(planQuotas.quotas.maxProductsPerVertical)}
          />
          <QuotaItem label="Max Users" value={String(planQuotas.quotas.maxUsersPerTenant)} />
          <QuotaItem
            label="Max API Requests/Day"
            value={planQuotas.quotas.maxApiRequestsPerDay.toLocaleString()}
          />
          <QuotaItem label="Max Storage" value={`${planQuotas.quotas.maxStorageMb} MB`} />
        </dl>
      </div>

      {/* Environments */}
      <div className="card">
        <h3 className="text-base font-semibold mb-3">Allowed Environments</h3>
        <div className="flex gap-2">
          {planQuotas.allowedEnvironments.map((env) => (
            <span
              key={env}
              className="inline-flex items-center rounded-full bg-success/10 px-3 py-1 text-sm font-medium text-success"
            >
              {env}
            </span>
          ))}
          {planQuotas.allowedEnvironments.length === 0 && (
            <p className="text-sm text-foreground-muted">No environments selected</p>
          )}
        </div>
      </div>

      {/* Features */}
      <div className="card">
        <h3 className="text-base font-semibold mb-3">Features</h3>
        <div className="grid gap-2 sm:grid-cols-2">
          {(Object.keys(planQuotas.features) as (keyof TenantFeatures)[]).map((feature) => (
            <div key={feature} className="flex items-center gap-2">
              {planQuotas.features[feature] ? (
                <CheckCircle className="h-4 w-4 text-success" />
              ) : (
                <XCircle className="h-4 w-4 text-foreground-muted" />
              )}
              <span className="text-sm">{FEATURE_LABELS[feature]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Internal Components
// ============================================================================

interface QuotaItemProps {
  readonly label: string;
  readonly value: string;
}

function QuotaItem({ label, value }: QuotaItemProps): ReactElement {
  return (
    <div className="flex justify-between">
      <dt className="text-sm text-foreground-muted">{label}</dt>
      <dd className="text-sm font-medium">{value}</dd>
    </div>
  );
}
